import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, FileText, Film, Settings, Sparkles, Trash2, Users } from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import BetaAlert from '../../components/common/BetaAlert';
import {
  ResponsiveAppShell,
  TaskyAlert,
  TaskyBadge,
  TaskyBadgeProps,
  TaskyButton,
  TaskyCard,
  TaskyEmptyState,
  TaskyIconButton,
  TaskyPageContainer,
  TaskySkeleton,
  TaskyTopBar,
  useConfirm,
} from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { usePolling } from '../../hooks/usePolling';
import { ProductSelectionData, ProductSelectionModal } from '../../components/image-generation';
import recipeVideoService, {
  RecipeVideoStatus,
  RecipeVideoStyle,
  RecipeVideoSummary,
  isRecipeVideoBusy,
  newIdempotencyKey,
} from '../../services/api/recipeVideoService';
import RecipeVideoProgressCard from './RecipeVideoProgressCard';
import RecipeVideoStyleSelectModal from './RecipeVideoStyleSelectModal';
import { useRecipeVideoStrings } from './strings';

/**
 * 動画スタイルレシピの一覧＋新規作成（UGC動画広告の一覧ページと同じ構成）。
 *
 * 上: 商品と訴求 → 公開中のスタイル → 「動画を作る」（ボタン1つで最後まで自動）。
 *     商品と訴求はバナー広告と同じ商品選択モーダル（ProductSelectionModal、戦略マップの「誰に、何を伝えるか」）、
 *     スタイルはバナーの「スタイルを選ぶ」と同じ見た目のモーダルで選ぶ（2026-10-15 ユーザー要望）
 * 「動画を作る」を押しても画面は移らない（バナーと同じ）。できたプロジェクトを一覧の先頭に入れ、
 *     「作成中」の区画に段ごとの進み具合（台本 → 人物 → 絵 n/m → 声 n/m → … → 仕上げ）を出す（2026-10-18 ユーザー要望）
 * 下: 作成した動画（完成・失敗）のグリッド。どのカードも押すと詳細（編集ページ）へ行く
 */

const STATUS_VARIANT: Record<RecipeVideoStatus, TaskyBadgeProps['variant']> = {
  pending: 'secondary',
  running: 'info',
  completed: 'success',
  failed: 'destructive',
};

/** 一覧のポーリング間隔（作成中のものがあるときだけ）。作った直後は台本など短い段が続くので細かく取る */
const LIST_POLL_MS = 5000;
const QUICK_POLL_MS = 2000;
const QUICK_POLL_WINDOW_MS = 30000;

const RecipeVideoListPage: React.FC = () => {
  const t = useRecipeVideoStrings();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [styles, setStyles] = useState<RecipeVideoStyle[]>([]);
  const [styleId, setStyleId] = useState<number | null>(null);
  // 商品と訴求はバナーと同じ商品選択モーダルで選ぶ（訴求は任意。選ばなければサーバーが商品の最初の訴求を使う）
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const productId = selectedProductData?.product.id ?? null;
  const appeal = selectedProductData?.appealName ?? '';
  const appealDirection = appeal ? selectedProductData?.appealDirection ?? '' : '';
  const appealSegment = appeal ? selectedProductData?.segmentName ?? '' : '';

  const [projects, setProjects] = useState<RecipeVideoSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  // 同じ選択で押し直したときは同じキーを送る（応答を受け取れずに押し直しても二重に作らない）。選択を変えたら新しいキー。
  // 押した時点の選択から決める（読み込み後の state 更新の順番でキーが変わらないように、effect では作り直さない）
  const createKeyRef = useRef<{ selection: string; key: string } | null>(null);
  const createKeyFor = (selection: string): string => {
    if (createKeyRef.current?.selection !== selection) {
      createKeyRef.current = { selection, key: newIdempotencyKey() };
    }
    return createKeyRef.current.key;
  };
  const [error, setError] = useState<string | null>(null);

  // 一覧の取得は世代番号で古い応答を捨てる（作成・削除より前に出た取得で、足したカードを消したり消したカードを戻したりしない）
  const listSeqRef = useRef(0);
  const loadProjects = useCallback(async () => {
    const seq = ++listSeqRef.current;
    const list = await recipeVideoService.getProjects();
    if (seq === listSeqRef.current) setProjects(list);
  }, []);

  useEffect(() => {
    loadProjects()
      .catch((e) => setError(recipeVideoService.extractErrorMessage(e)))
      .finally(() => setIsLoading(false));
    recipeVideoService
      .getStyles()
      .then((list) => {
        setStyles(list);
        if (list.length > 0) setStyleId(list[0].id);
      })
      .catch((e) => setError(recipeVideoService.extractErrorMessage(e)));
  }, [loadProjects]);

  // 作成中のものがある間だけ一覧を取り直す。作った直後の30秒は短い間隔
  const busyProjects = projects.filter((p) => isRecipeVideoBusy(p.status));
  const doneProjects = projects.filter((p) => !isRecipeVideoBusy(p.status));
  const [quickPolling, setQuickPolling] = useState(false);
  const quickTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (quickTimerRef.current !== null) window.clearTimeout(quickTimerRef.current);
  }, []);
  const { run: refreshProjects } = usePolling(loadProjects, {
    enabled: busyProjects.length > 0,
    intervalMs: quickPolling ? QUICK_POLL_MS : LIST_POLL_MS,
  });

  const handleCreate = async () => {
    if (isCreating || productId === null || styleId === null) return;
    setIsCreating(true);
    setError(null);
    try {
      const project = await recipeVideoService.createProject({
        product_id: productId,
        video_style_recipe_id: styleId,
        appeal: appeal || undefined,
        appeal_direction: appealDirection || undefined,
        appeal_segment: appealSegment || undefined,
        idempotency_key: createKeyFor(JSON.stringify([productId, styleId, appeal, appealDirection, appealSegment])),
      });
      createKeyRef.current = null;
      // 画面は移らず、一覧の先頭（作成中の区画）に出す。同じキーの再送で同じプロジェクトが返っても重ねない
      listSeqRef.current += 1;
      setProjects((prev) => [project, ...prev.filter((p) => p.id !== project.id)]);
      showToast(t.created);
      setQuickPolling(true);
      if (quickTimerRef.current !== null) window.clearTimeout(quickTimerRef.current);
      quickTimerRef.current = window.setTimeout(() => setQuickPolling(false), QUICK_POLL_WINDOW_MS);
      void refreshProjects();
    } catch (e) {
      setError(recipeVideoService.isCreditInsufficientError(e) ? t.creditShort : recipeVideoService.extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (project: RecipeVideoSummary) => {
    if (!(await confirm({ title: t.deleteConfirm(project.title), tone: 'destructive', confirmText: t.delete }))) return;
    try {
      await recipeVideoService.deleteProject(project.id);
      listSeqRef.current += 1;
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      setError(recipeVideoService.extractErrorMessage(err));
    }
  };

  const selectedStyle = styles.find((s) => s.id === styleId) ?? null;

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle={t.pageTitle}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg lg:h-screen lg:min-h-0">
        <TaskyTopBar
          title={t.pageTitle}
          icon={<Clapperboard className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            <BetaAlert featureName="動画生成" />

            {error && (
              <TaskyAlert tone="error" className="mb-4">
                {error}
              </TaskyAlert>
            )}

            {/* 新規作成 */}
            <TaskyCard variant="warm" padding="md" className="mb-6" data-testid="recipe-video-create">
              <div className="mb-4">
                <h2 className="text-base font-semibold text-dark">{t.createTitle}</h2>
                <p className="mt-1 text-sm text-gray-500">{t.createLead}</p>
              </div>

              <div className="space-y-5">
                {/* 商品情報（バナー広告の「商品情報」と同じ見た目・同じモーダル） */}
                <div className="rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-3" data-testid="recipe-video-product">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 flex-shrink-0 text-orange-600" />
                      <span className="text-sm font-medium text-gray-900">{t.productInfo}</span>
                      {selectedProductData && (
                        <span className="truncate rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-600">{selectedProductData.product.name}</span>
                      )}
                    </div>
                    <TaskyButton type="button" size="sm" onClick={() => setShowProductModal(true)} className="flex-shrink-0">
                      <Sparkles className="h-3.5 w-3.5" />
                      {selectedProductData ? t.changeProduct : t.selectProduct}
                    </TaskyButton>
                  </div>
                  {selectedProductData && (
                    <div className="mt-3 border-t border-orange-200 pt-3">
                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
                        <Users className="h-3.5 w-3.5 text-gray-500" />
                        <span>{t.appealLabel}</span>
                        <span className="rounded bg-violet-100 px-1.5 py-0.5 font-medium text-violet-700" data-testid="recipe-video-appeal">
                          {appeal ? [appealSegment, appeal].filter(Boolean).join(' / ') : t.appealAuto}
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-gray-500">{t.appealNote}</p>
                    </div>
                  )}
                </div>

                {/* 動画のスタイル（バナーの「スタイルを選ぶ」と同じ見た目） */}
                <div className="min-w-0">
                  <p className="mb-2 text-sm font-semibold text-gray-700">{t.style}</p>
                  {styles.length === 0 ? (
                    <p className="text-sm text-gray-500">{t.noStyles}</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <TaskyButton type="button" variant="outline" onClick={() => setShowStyleModal(true)}>
                        <Settings />
                        {t.selectStyle}
                      </TaskyButton>
                      {selectedStyle && (
                        <div className="flex max-w-full items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5" data-testid="recipe-video-selected-style">
                          <span className="truncate text-sm font-medium text-blue-700">{selectedStyle.name}</span>
                          <span className="whitespace-nowrap text-xs text-blue-600">{t.estimate(selectedStyle.estimated_credits)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-gray-500">{t.styleNote}</p>
                </div>

                <div className="flex items-center justify-end gap-3 pt-1">
                  {selectedStyle && <span className="text-xs text-gray-500">{t.estimate(selectedStyle.estimated_credits)}</span>}
                  <TaskyButton
                    type="button"
                    onClick={handleCreate}
                    isLoading={isCreating}
                    disabled={productId === null || styleId === null}
                  >
                    <Sparkles className="h-4 w-4" />
                    {isCreating ? t.creating : t.create}
                  </TaskyButton>
                </div>
              </div>
            </TaskyCard>

            {/* 作成中（段ごとの進み具合） */}
            {busyProjects.length > 0 && (
              <section className="mb-6" aria-labelledby="recipe-video-busy-title" data-testid="recipe-video-busy">
                <h2 id="recipe-video-busy-title" className="mb-3 text-sm font-semibold text-dark">
                  {t.busyTitle}
                </h2>
                {/* grid-cols-1（minmax(0,1fr)）が無いと、スマホでは1行のタイトルの幅まで列が広がりカードが見切れる */}
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {busyProjects.map((project) => (
                    <RecipeVideoProgressCard key={project.id} project={project} />
                  ))}
                </div>
              </section>
            )}

            {/* 作成した動画（完成・失敗） */}
            {(isLoading || doneProjects.length > 0 || busyProjects.length === 0) && (
              <h2 className="mb-3 text-sm font-semibold text-dark">{t.listTitle}</h2>
            )}
            {isLoading ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <TaskySkeleton key={i} className="aspect-[9/16] w-full" />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <TaskyEmptyState icon={<Film />} title={t.emptyTitle} />
            ) : (
              doneProjects.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {doneProjects.map((project) => (
                    <TaskyCard
                      key={project.id}
                      variant="bordered"
                      padding="none"
                      hoverable
                      className="relative overflow-hidden"
                      data-testid={`recipe-video-card-${project.id}`}
                    >
                      <div className="flex aspect-[9/16] items-center justify-center overflow-hidden bg-gray-100">
                        {project.thumbnail_url ? (
                          <img src={project.thumbnail_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Film className="h-10 w-10 text-gray-300" />
                        )}
                      </div>
                      <div className="p-3">
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <h3 className="truncate text-sm font-semibold text-dark">
                            {/* カード全体を押せるようにリンクを広げる（削除ボタンはその上に重ねる） */}
                            <Link
                              to={`/ads/recipe-video/${project.id}`}
                              className="rounded-sm after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                              {project.title}
                            </Link>
                          </h3>
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(project)}
                            className="relative z-10 shrink-0 text-gray-400 hover:text-red-500"
                            title={t.delete}
                            aria-label={t.delete}
                          >
                            <Trash2 />
                          </TaskyIconButton>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <TaskyBadge variant={STATUS_VARIANT[project.status]}>{t.status[project.status]}</TaskyBadge>
                          {project.output_seconds !== null && <span>{t.seconds(project.output_seconds)}</span>}
                        </div>
                        {project.status === 'failed' && (
                          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-red-600">{project.error || t.failedTitle}</p>
                        )}
                      </div>
                    </TaskyCard>
                  ))}
                </div>
              )
            )}
          </TaskyPageContainer>
        </div>
      </div>
      {showProductModal && (
        <ProductSelectionModal
          isOpen={showProductModal}
          purpose="video"
          onClose={() => setShowProductModal(false)}
          onConfirm={(data) => {
            setSelectedProductData(data);
            setShowProductModal(false);
          }}
        />
      )}

      <RecipeVideoStyleSelectModal
        isOpen={showStyleModal}
        styles={styles}
        selectedId={styleId}
        onClose={() => setShowStyleModal(false)}
        onSelect={(style) => {
          setStyleId(style.id);
          setShowStyleModal(false);
        }}
      />
    </ResponsiveAppShell>
  );
};

export default RecipeVideoListPage;
